import type { GridConfig } from "@/types/grid";
import type { BulkImportPageSetupStatic } from "@/types/bulk-import";

export const getWorkGroupsPageConfig =
  (): BulkImportPageSetupStatic<WorkGroupSchema> => ({
    id: "work-groups",
    pageTitle: "cksetup.imports.work-groups.pageTitle",
    pageHeading: "cksetup.imports.work-groups.heading",
    learnMoreTitle: "cksetup.imports.work-groups.learnMoreTitle",
    learnMoreText: "cksetup.imports.work-groups.learnMoreText",
    templateFileName: "cksetup.imports.work-groups.templateName",
    templateWorksheetName: "tableColumns.workGroups.worksheetName",
    tabTitle: "cksetup.imports.bulk.workGroupsHeading",
    tabDescription: "cksetup.imports.bulk.workGroupsDescription",
    imageSrc: "/static/images/bulk/work-groups.png",
    validationSchema: workGroupSchema,
    addButtonLabel: "cksetup.imports.work-groups.addNewWorkGroupLine",
  });

export const changeOverLevels = {
  noChangeover: 0,
  nonAllergenChangeover: 1,
  allergenChangeover: 2,
  severeAllergenChangeover: 3,
} as const;

export const getChangeOverLevelOptions = (): ValueLabelPair<number>[] => {
  return Object.entries(changeOverLevels).map(([key, value]) => ({
    value,
    label: `cksetup.imports.work-groups.${key}`,
  }));
};

export const getWorkGroupsGridConfig = async (): Promise<
  GridConfig<WorkGroupSchema>
> => {
  return {
    mutateDependantsFn: (data, events) => {
      events.forEach((event) => {
        if (
          event.type === "updated" &&
          event.field === "changeoverLevel" &&
          event.dataItem.changeoverLevel === changeOverLevels.noChangeover &&
          !event.dataItem.systemCreated &&
          data[event.rowIndex]
        ) {
          data[event.rowIndex]!.defaultAllSelection = true;
          data[event.rowIndex]!.$dirty = {
            ...(data[event.rowIndex]!.$dirty ?? {}),
            defaultAllSelection: true,
          };
        }
      });

      return { data, events };
    },
    computeItemFn: (item) => {
      if (!item.systemCreated) return item;

      return {
        ...item,
        $disableRowRemoval: true,
        $readonly: {
          name: false,
          active: true,
          comment: true,
          changeoverLevel: true,
          defaultAllSelection: true,
        },
      };
    },
    defaultRowData: {
      changeoverLevel: changeOverLevels.noChangeover,
      defaultAllSelection: true,
      active: true,
    },
    columns: [
      {
        field: "number",
        alternativeMappingNames: ["id"],
        hidden: true,
        isKey: true,
        propagateErrorsTo: "name",
      },
      {
        field: "name",
        title: "tableColumns.workGroups.name",
        helperText: "tableColumns.workGroups.nameHelperText",
        placeholder: "tableColumns.enterName",
        editor: "text",
      },
      {
        field: "changeoverLevel",
        title: "tableColumns.changeover",
        helperText: "tableColumns.workGroups.changeoverLevelHelperText",
        editor: "select",
        placeholder: "tableColumns.select",
        editorProps: {
          translateItems: true,
          items: getChangeOverLevelOptions(),
        },
        minWidthPx: 220,
      },
      {
        field: "active",
        title: "tableColumns.activeColumnLabel",
        helperText: "tableColumns.workGroups.activeHelperText",
        editor: "boolean",
        minWidthPx: 110,
      },
      {
        field: "defaultAllSelection",
        title: "tableColumns.workGroups.defaultAllSelection",
        helperText: "tableColumns.workGroups.defaultAllSelectionHelperText",
        editor: "boolean",
        minWidthPx: 200,
      },
      {
        field: "comment",
        title: "tableColumns.workGroups.comment",
        helperText: "tableColumns.workGroups.commentsHelperText",
        editor: "text",
        placeholder: "tableColumns.enterComments",
        required: false,
      },
    ],
  };
};
